/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import StoreDataContext from "src/app/context/store-data";
import isLiveChatAvailable from "src/app/utilities/isLiveChatAvailable";
import isFeatureEnabled from "src/app/utilities/isFeatureEnabled";
import isOutOfHours from "src/app/utilities/isOutOfHours";
import Main from "src/app/components/Main/Main";
import Aside from "src/app/components/Aside/Aside";
import Card from "src/app/components/Card/Card";
import Hero from "src/app/components/Hero/Hero";
import MyAccount from "src/app/components/MyAccount/MyAccount";
import ContactChannels from "src/app/components/ContactChannels/ContactChannels";
import ViewRelatedQuestions from "src/app/components/ViewRelatedQuestions/ViewRelatedQuestions";
import ChatBot from "src/app/components/ChatBot";
import WithLoader from "src/app/components/WithLoader";
import GetInTouchInfoMessage from "src/app/components/GetInTouchInfoMessage";
import noticeError from "src/app/utilities/noticeError";
import { OUT_OF_HOURS_MARKETS } from "src/shared/constants";

export default function GetInTouch({ content, storeData }) {
  const [loading, setLoading] = useState(true);
  const [liveChatAvailable, setLiveChatAvailable] = useState(false);
  const [chatBotEnabled, setChatBotEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [outOfHours, setOutOfHours] = useState(false);
  const [chatBotStatus, setChatBotStatus] = useState("loading");
  const { chatBotMinimised } = useSelector((state) => state.getInTouch);
  const { language, storeCode } = useContext(StoreDataContext);
  const body = content?.main?.body;
  const aside = content?.aside;
  const heroText = body?.elements[0]?.header.text;
  const heroIsCaps = body?.elements[0]?.header.isCaps;
  const heroMediumImage = body?.elements[0]?.header["image-medium"]?.src;
  const heroLargeImage = body?.elements[0]?.header["image-large"]?.src;
  const myAccountTitle = body?.elements[0]?.header?.["myaccount-title"];
  const myAccountButton = body?.elements[0]?.header?.["myaccount-button"];
  const myAccountSubHeading =
    body?.elements[0]?.header?.["myaccount-subHeading"];
  const popularArticles = aside?.["popular-articles"]?.elements[0];
  const chatNowChannel = body.elements[0]?.["chat-channel"];
  const channels = body.elements[0]?.channels;
  const chatBotActive = chatBotStatus === "chatactive";
  const isOutOfHoursMarket = OUT_OF_HOURS_MARKETS.includes(storeCode);
  const showHero =
    !chatBotEnabled ||
    (isOutOfHoursMarket && outOfHours) ||
    chatBotStatus !== "chatactive";

  function handleChatBotStatusChange(status) {
    setChatBotStatus(status);
  }

  useEffect(() => {
    async function load() {
      try {
        const liveChatAvailable = await isLiveChatAvailable(
          storeCode,
          language
        );
        const chatBotEnabled = await isFeatureEnabled("cchp-chat-bot");
        const emailEnabled = await isFeatureEnabled(
          "cchp-show-send-us-an-email"
        );
        const outOfHours = await isOutOfHours({ storeCode });

        setLiveChatAvailable(liveChatAvailable);
        setChatBotEnabled(chatBotEnabled);
        setEmailEnabled(emailEnabled);
        setChatBotStatus(
          !chatBotEnabled || (isOutOfHoursMarket && outOfHours)
            ? "disabled"
            : "loading"
        );
        setOutOfHours(outOfHours);
        setLoading(false);
      } catch (error) {
        noticeError(error);
      }
    }

    load();
  }, []);

  return (
    <>
      <Main>
        <Card type="getInTouch" ariaLive="polite" ariaRelevant="additions text">
          {showHero && (
            <>
              <Hero
                text={heroText}
                isCaps={heroIsCaps}
                mediumImage={heroMediumImage}
                largeImage={heroLargeImage}
                type="getInTouch"
              />
              <MyAccount
                title={myAccountTitle}
                description={myAccountSubHeading}
                buttonText={myAccountButton}
              />
            </>
          )}
          <WithLoader
            loading={loading || chatBotStatus === "loading"}
            style={{ height: "calc(100% - 12px)" }}
          >
            <GetInTouchInfoMessage
              outOfHours={outOfHours}
              liveChatAvailable={liveChatAvailable}
              storeCode={storeCode}
            />
            {chatBotEnabled && (!isOutOfHoursMarket || !outOfHours) && (
              <ChatBot
                isActive={chatBotActive}
                isMinimised={chatBotMinimised}
                onStatusChange={handleChatBotStatusChange}
                status={chatBotStatus}
              />
            )}
            {!chatBotEnabled && (
              <ul>
                <ContactChannels
                  chatNow={chatNowChannel}
                  channels={channels}
                  storeData={storeData}
                  liveChatAvailable={
                    liveChatAvailable && (!isOutOfHoursMarket || !outOfHours)
                  }
                  showEmailButton={emailEnabled || !liveChatAvailable}
                />
              </ul>
            )}
          </WithLoader>
        </Card>
      </Main>
      <Aside>
        <Card type="aside">
          <ViewRelatedQuestions
            data={popularArticles}
            showButton={false}
            visibleItems={6}
          />
        </Card>
      </Aside>
    </>
  );
}

GetInTouch.propTypes = {
  content: PropTypes.object,
  storeData: PropTypes.object,
};
